export type LawyerRanking = {
  contactId: string | null;
  rankingTier: number | null;
  rankingType: string | null;
  tableName: string | null;
};

export const PREVIOUS_TIER_UNAVAILABLE = "—";

/** The badge's empty states, owned here because this is where they are produced. */
export const NOT_RANKED_YET_LABEL = "Not ranked yet";
export const TIER_UNAVAILABLE_LABEL = "Tier unavailable";

/**
 * The labels the tier badge renders muted — the two states that are an absence rather than a
 * ranking. Exported so the banner styles off the same strings it displays: it previously kept its
 * own hardcoded copy, and renaming a label here left the styling silently unapplied with no test
 * going red. Task 0048 item 10.
 */
export const MUTED_TIER_LABELS: ReadonlySet<string> = new Set([
  NOT_RANKED_YET_LABEL,
  TIER_UNAVAILABLE_LABEL,
]);

function isRealTier(rankingTier: number | null): rankingTier is number {
  return rankingTier !== null && rankingTier >= 1;
}

/**
 * The business name for each lawyer ranking category, mirroring the org list's `CATEGORY_LABELS`
 * (`rankingBadge.ts`). Without it `humanizeRankingCategory` title-cases the raw value and produces
 * "Next Generation" for what the business calls Next Generation Partners. Names taken from the
 * prototype's ranking-table type list. Keyed on the value with its grain prefix stripped, so both
 * stored forms resolve. Task 0048 item 5.
 */
const CATEGORY_LABELS: Record<string, string> = {
  hall_of_fame: "Hall of Fame",
  leading_individuals: "Leading Individuals",
  next_generation: "Next Generation Partners",
  rising_stars: "Rising Stars",
  rising_stars2: "Rising Stars",
  new_silks: "New Silks",
  new_silks_2025: "New Silks",
  new_silks_2026: "New Silks",
  leading_juniors: "Leading Juniors",
  leading_silks: "Leading Silks",
  recommended: "Recommended",
  leading_lawyers: "Leading Lawyers",
};

function withoutGrainPrefix(rankingType: string): string {
  return rankingType.replace(/^lawyer[\s_]+/, "");
}

function humanizeRankingCategory(rankingType: string | null): string {
  if (!rankingType) {
    return PREVIOUS_TIER_UNAVAILABLE;
  }

  const bare = withoutGrainPrefix(rankingType);

  return (
    CATEGORY_LABELS[bare] ??
    bare
      .split("_")
      .filter(word => word.length > 0)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ")
  );
}

export type RankingBadgeFields = Pick<LawyerRanking, "rankingTier" | "rankingType">;

export function formatRankingBadge(ranking: RankingBadgeFields | null): string {
  if (!ranking) {
    return PREVIOUS_TIER_UNAVAILABLE;
  }

  if (isRealTier(ranking.rankingTier)) {
    return String(ranking.rankingTier);
  }

  if (ranking.rankingTier === 0) {
    return humanizeRankingCategory(ranking.rankingType);
  }

  return PREVIOUS_TIER_UNAVAILABLE;
}

export type LawyerTierLabelInput = {
  ranking: LawyerRanking | null;
  rankingsUnavailable: boolean;
};

export function lawyerTierLabel({ ranking, rankingsUnavailable }: LawyerTierLabelInput): string {
  if (rankingsUnavailable) {
    return TIER_UNAVAILABLE_LABEL;
  }

  if (ranking === null) {
    return NOT_RANKED_YET_LABEL;
  }

  if (isRealTier(ranking.rankingTier)) {
    return `Tier ${ranking.rankingTier}`;
  }

  // Not a real tier: an untiered category band or an unreadable tier both fall through to
  // formatRankingBadge, which already knows how to humanise the category (or fall back to a dash).
  return formatRankingBadge(ranking);
}

export type PreviousTierHistoryLineInput = {
  rankingTier: number | null;
  rankingType: string | null;
  editionName: string;
};

/**
 * Names the edition a previous tier came from, e.g. "Tier 4 in the 2026 EMEA Edition". Tier 0 is
 * the recommended-but-untiered band, so it is never rendered as the numeral 0 — the category name
 * (via humanizeRankingCategory, the same helper formatRankingBadge uses) stands in for it instead.
 */
export function previousTierHistoryLine({
  rankingTier,
  rankingType,
  editionName,
}: PreviousTierHistoryLineInput): string {
  const tierLabel = isRealTier(rankingTier)
    ? `Tier ${rankingTier}`
    : humanizeRankingCategory(rankingType);

  return `${tierLabel} in the ${editionName}`;
}
